// ============================================================
// routeByItemType.ts
// Shared deep-link contract for CalendarEntry items (tasks +
// case-deadlines) rendered anywhere in the app. Currently
// consumed by DashboardView's YOUR NEXT DEADLINES rows only —
// CalendarView has its own local per-row handlers (handleOpenCase
// / handleOpenTask / handleOpenClient) rather than routing through
// here. The Stage 3c capture pattern lives in those local handlers;
// this helper's `closeAllTakeovers()` call at the case-open path
// (~L84) supplies the equivalent capture on the Dashboard side.
//
// Routing rules — driven by CalendarEntry.type + the presence
// of caseId / taskId:
//   1. type === 'case_deadline' && caseId → open the case AT the
//      deadlines section. The section-anchor is applied by
//      ViewingPane's mount effect, which reads the
//      caseInitialSection intent set here.
//   2. type === 'task' && caseId → same as above (the task lives
//      against a case → the user's most useful landing is the
//      case's deadlines section, where they can see it in context
//      alongside sibling deadlines). Behaviour change from the
//      pre-Stage flow, where a Calendar task-with-case click
//      opened the task in edit-form mode.
//   3. type === 'task' && !caseId → personal to-do; open Tasks
//      view + highlight/scroll to the row. taskIntentId intent
//      is set here; TasksView's mount effect consumes + clears.
//   4. Anything else (no caseId AND no taskId) → console.warn
//      no-op. The item shouldn't render in a click-throughable
//      list in the first place, but the guard is cheap.
//
// FLUSH-FIRST discipline: mirrors CalendarView L294-304 and every
// Sidebar nav handler. Any pending case / client / notes / witness /
// deadline autosave is drained BEFORE the nav triggers a route change
// so the user's last keystroke isn't lost mid-transition.
//
// INTENT CLEAR discipline: neither intent is cleared by this
// helper. That's DELIBERATE — the target view's mount effect owns
// the clear, so the intent survives across the async gap between
// "we set it here" and "the target view mounts and reads it".
// (Store field docblocks in useCasevoStore.ts document the same
// contract from the reader's side.)
// ============================================================
import { CalendarEntry } from '../types/calendar.types'
import { useCasevoStore } from '../store/useCasevoStore'
import { navigateTo } from '../navigation/navigate'
import { flushAutoSave } from '../hooks/useAutoSave'
import { flushClientAutoSave } from '../hooks/useClientAutoSave'
import { flushClientNotesAutoSave } from '../hooks/useClientNotesAutoSave'
import { flushWitnessAutoSave } from '../hooks/useWitnessAutoSave'
import { flushDeadlineAutoSave } from '../hooks/useCaseDeadlineAutoSave'

// Source enumerates every view that can trigger this helper. Both
// values are recognised by CaseOpenedFrom and TaskOpenedFrom, so
// the case-detail Back button and TasksView's Back button both
// return the user to whichever list they clicked the item on.
export type RouteSource = 'dashboard' | 'calendar'

// Standard flush pattern — drains all five autosave streams in
// parallel. Failure of any single stream is caught inside the
// underlying flush impl; this Promise.all resolves once every
// stream has settled (success or swallowed error).
const flushAll = (): Promise<unknown> =>
  Promise.all([
    flushAutoSave(),
    flushClientAutoSave(),
    flushClientNotesAutoSave(),
    flushWitnessAutoSave(),
    flushDeadlineAutoSave(),
  ])

export const routeByItemType = async (
  entry: CalendarEntry,
  source: RouteSource,
): Promise<void> => {
  await flushAll()
  const store = useCasevoStore.getState()

  // Case-deadline OR task-with-caseId → open the case at deadlines.
  // Both types land here because the semantically-useful destination
  // is the same: "show me this deadline in the context of its case".
  // The task-with-caseId case would otherwise route to Tasks view
  // for editing, but the routing helper's design says: if a case is
  // available, the case wins.
  if (entry.caseId) {
    // Close the takeover FIRST (closeAllTakeovers nulls currentCase, so it
    // can't follow the load) → the case page's loading shell shows at once
    // instead of the takeover freezing for the whole case + satellite load.
    // The trail snapshot is taken before the flags drop, so Back still
    // returns to the source view. A blocked/missing case lands on the case
    // list with the store's toast (setCurrentCaseById handles 403/404).
    const caseId = entry.caseId
    try {
      await navigateTo({ view: 'caseDetail', caseId }, { skipFlush: true, intents: (s) => { s.setCaseOpenedFrom(source); s.setCaseInitialSection('deadlines') } })
    } catch (err) {
      // Case fetch failed — the loading shell falls back to the case
      // list once isCaseLoading clears (no stranded empty case page). Clear the intent
      // we just set so a subsequent successful case-open doesn't
      // inherit it.
      store.setCaseInitialSection(null)
      // eslint-disable-next-line no-console
      console.error('[routeByItemType] Failed to open case:', err)
    }
    return
  }

  // Task without caseId → personal to-do. Open Tasks view and
  // deep-link to the row via taskIntentId (highlight + scroll).
  if (entry.type === 'task' && entry.taskId) {
    const taskId = entry.taskId
    await navigateTo({ view: 'tasks' }, { skipFlush: true, intents: (s) => { s.setTaskIntentId(taskId); s.setTaskOpenedFrom(source) } })
    return
  }

  // Unroutable — the entry lacks both a caseId and a taskId. Log
  // and no-op. In practice the backend calendar controller always
  // populates one or the other for either entry type; if this
  // fires it's a data-shape bug worth surfacing.
  // eslint-disable-next-line no-console
  console.warn('[routeByItemType] Unroutable entry (no caseId, no taskId):', entry)
}
